import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckSquare, Plus, Trash, SunHorizon, MoonStars, ListChecks, Lightning, SpinnerGap, Check, CaretDown, CaretUp, ArrowsClockwise, FloppyDisk, Copy, ShareNetwork } from "@phosphor-icons/react";
import { MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  opening: { icon: SunHorizon, label: "Abertura", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  closing: { icon: MoonStars, label: "Fechamento", color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
  production: { icon: Lightning, label: "Produção", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  custom: { icon: ListChecks, label: "Personalizado", color: "text-zinc-600", bg: "bg-zinc-50", border: "border-zinc-200" },
};

export default function Checklists() {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("custom");
  const [newItems, setNewItems] = useState([""]);
  const [expandedId, setExpandedId] = useState(null);
  const [pracas, setPracas] = useState([]);
  const [filterPraca, setFilterPraca] = useState("all");
  const [genDialog, setGenDialog] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [events, setEvents] = useState([]);
  const [genSelectedIds, setGenSelectedIds] = useState([]);
  const [genSelectedEvent, setGenSelectedEvent] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const { t } = useLanguage();

  useEffect(() => { fetchChecklists(); fetchPracas(); }, []);

  const fetchChecklists = async () => {
    try {
      const { data } = await axios.get(`${API}/checklists`);
      setChecklists(data);
    } catch { toast.error("Erro ao carregar checklists"); }
    finally { setLoading(false); }
  };

  const fetchPracas = async () => {
    try {
      const { data } = await axios.get(`${API}/pracas`);
      setPracas(data || []);
    } catch { /* silent - pracas may not exist */ }
  };

  const openGenDialog = async () => {
    setGenDialog(true);
    setGenSelectedIds([]);
    setGenSelectedEvent("");
    try {
      const [sheetsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/technical-sheets`),
        axios.get(`${API}/events`).catch(() => ({ data: [] })),
      ]);
      setSheets(sheetsRes.data || []);
      setEvents(eventsRes.data || []);
    } catch { /* silent */ }
  };

  const generateFromSheets = async () => {
    if (genSelectedIds.length === 0 && !genSelectedEvent) {
      toast.error("Selecione fichas ou um evento"); return;
    }
    setGenLoading(true);
    try {
      const selectedSheets = sheets.filter(s => genSelectedIds.includes(s.id));
      const sheetNames = selectedSheets.map(s => s.name).join(", ");
      const eventInfo = genSelectedEvent ? events.find(e => e.id === genSelectedEvent) : null;
      let prompt = eventInfo
        ? `Gere um checklist de produção completo para o evento "${eventInfo.name}" (${eventInfo.num_guests || '?'} convidados). Inclua mise en place, produção, verificação e finalização.`
        : `Gere um checklist de produção para: ${sheetNames}. Inclua mise en place com quantidades, etapas de produção, verificações e finalização.`;
      const { data } = await axios.post(`${API}/sous-chef/chat`, { message: prompt, current_page: "/checklists" });
      const match = data.response?.match(/```checklist\s*([\s\S]*?)\s*```/);
      if (match) {
        const parsed = JSON.parse(match[1]);
        const items = (parsed.items || []).map(text => ({ text, checked: false, category: "" }));
        await axios.post(`${API}/checklists`, {
          name: parsed.name || `Produção — ${(eventInfo?.name || sheetNames).substring(0, 40)}`,
          checklist_type: parsed.type || "production", items,
        });
        toast.success("Checklist gerada pela IA!");
        setGenDialog(false);
        fetchChecklists();
      } else {
        toast.error("IA não gerou formato esperado. Tente pelo Sous-Chef.");
      }
    } catch { toast.error("Erro ao gerar checklist"); }
    finally { setGenLoading(false); }
  };

  const createFromTemplate = async (type) => {
    try {
      await axios.post(`${API}/checklists/from-template?template_type=${type}`);
      toast.success(type === "opening" ? "Checklist de Abertura criada!" : "Checklist de Fechamento criada!");
      fetchChecklists();
    } catch { toast.error("Erro ao criar checklist"); }
  };

  const createCustom = async () => {
    if (!newName.trim()) return;
    const items = newItems.filter(t => t.trim()).map(text => ({ id: crypto.randomUUID?.() || Date.now().toString(), text, checked: false, category: null }));
    if (items.length === 0) { toast.error("Adicione pelo menos 1 item"); return; }
    try {
      await axios.post(`${API}/checklists`, { name: newName, checklist_type: newType, items });
      toast.success("Checklist criada!");
      setDialogOpen(false);
      setNewName(""); setNewItems([""]); setNewType("custom");
      fetchChecklists();
    } catch { toast.error("Erro ao criar"); }
  };

  const toggleItem = async (checklist, itemIdx) => {
    const updated = checklist.items.map((item, i) => i === itemIdx ? { ...item, checked: !item.checked } : item);
    try {
      const { data } = await axios.put(`${API}/checklists/${checklist.id}/execute`, { items: updated });
      setChecklists(prev => prev.map(c => c.id === checklist.id ? { ...c, items: updated, completed: data.completed } : c));
      if (data.completed) toast.success("Checklist completa!");
    } catch { toast.error("Erro ao atualizar"); }
  };

  const deleteChecklist = async (id) => {
    try {
      await axios.delete(`${API}/checklists/${id}`);
      setChecklists(prev => prev.filter(c => c.id !== id));
      toast.success("Checklist removida");
    } catch { toast.error("Erro ao remover"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  const active = checklists.filter(c => !c.completed && !c.is_template);
  const completed = checklists.filter(c => c.completed && !c.is_template);
  const templates = checklists.filter(c => c.is_template);

  const saveAsTemplate = async (id) => {
    try {
      await axios.post(`${API}/checklists/${id}/save-as-template`, { recurrence: "daily" });
      toast.success("Salvo como template diário!");
      fetchChecklists();
    } catch { toast.error("Erro ao salvar template"); }
  };

  const shareChecklist = async (id) => {
    try {
      const { data } = await axios.post(`${API}/share`, { type: "checklist", item_id: id });
      const shareUrl = `${APP_URL}/share/${data.share_code}`;
      if (navigator.share) {
        await navigator.share({ title: "Checklist", url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copiado!");
      }
    } catch { toast.error("Erro ao gerar link"); }
  };

  const applyTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/checklists/${templateId}/use-template`);
      toast.success("Checklist do dia criada!");
      fetchChecklists();
    } catch { toast.error("Erro ao criar a partir do template"); }
  };

  const assignToPraca = async (checklistId, pracaId) => {
    try {
      if (pracaId === "none") {
        await axios.post(`${API}/pracas/unassign`, { type: "checklist", item_id: checklistId });
      } else {
        await axios.put(`${API}/pracas/${pracaId}/assign`, { type: "checklist", item_id: checklistId });
      }
      toast.success("Praça atualizada!");
      fetchChecklists();
    } catch { toast.error("Erro ao atribuir praça"); }
  };

  // Apply praça filter
  const filterByPraca = (list) => {
    if (filterPraca === "all") return list;
    if (filterPraca === "none") return list.filter(c => !c.praca_id);
    return list.filter(c => c.praca_id === filterPraca);
  };

  const filteredActive = filterByPraca(active);
  const filteredCompleted = filterByPraca(completed);
  const filteredTemplates = filterByPraca(templates);

  return (
    <div className="space-y-6" data-testid="checklists-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
            <CheckSquare size={24} weight="bold" /> Checklists
          </h2>
          <p className="text-sm text-zinc-500 mt-1">Organize a rotina da sua operação</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {pracas.length > 0 && (
            <select value={filterPraca} onChange={(e) => setFilterPraca(e.target.value)}
              className="h-9 text-xs border border-zinc-200 rounded-md px-2 bg-white" data-testid="filter-praca-checklists">
              <option value="all">Todas Praças</option>
              <option value="none">Sem Praça</option>
              {pracas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <Button size="sm" variant="outline" onClick={() => createFromTemplate("opening")} className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="template-opening-btn">
            <SunHorizon size={16} className="mr-1.5" /> Abertura
          </Button>
          <Button size="sm" variant="outline" onClick={() => createFromTemplate("closing")} className="text-indigo-700 border-indigo-200 hover:bg-indigo-50" data-testid="template-closing-btn">
            <MoonStars size={16} className="mr-1.5" /> Fechamento
          </Button>
          <Button size="sm" variant="outline" onClick={openGenDialog} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="gen-checklist-btn">
            <Lightning size={16} className="mr-1.5" /> Gerar da IA
          </Button>
          <Button size="sm" onClick={() => setDialogOpen(true)} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="new-checklist-btn">
            <Plus size={16} className="mr-1.5" /> Nova Checklist
          </Button>
        </div>
      </div>

      {checklists.length === 0 && (
        <Card className="border-dashed border-zinc-300">
          <CardContent className="py-12 text-center">
            <CheckSquare size={40} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500 font-medium">Nenhuma checklist criada</p>
            <p className="text-xs text-zinc-400 mt-1">Crie uma checklist de abertura, fechamento ou produção para começar</p>
          </CardContent>
        </Card>
      )}

      {checklists.length > 0 && filteredActive.length === 0 && filteredCompleted.length === 0 && filteredTemplates.length === 0 && (
        <Card className="border-dashed border-zinc-300">
          <CardContent className="py-8 text-center">
            <MapPin size={32} className="mx-auto text-zinc-300 mb-2" />
            <p className="text-zinc-500 text-sm font-medium">Nenhuma checklist nesta praça</p>
            <p className="text-xs text-zinc-400 mt-1">Atribua checklists a esta praça ou selecione &quot;Todas Praças&quot;</p>
          </CardContent>
        </Card>
      )}

      {/* Saved Templates (recurring) */}
      {filteredTemplates.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-violet-600 flex items-center gap-1.5">
            <ArrowsClockwise size={12} weight="bold" /> Templates Recorrentes ({filteredTemplates.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {filteredTemplates.map((tpl) => {
              const config = TYPE_CONFIG[tpl.checklist_type] || TYPE_CONFIG.custom;
              const Icon = config.icon;
              return (
                <Card key={tpl.id} className="border-violet-100 bg-violet-50/30" data-testid={`template-${tpl.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={16} className={config.color} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-zinc-900 truncate">{tpl.name}</p>
                      <p className="text-[10px] text-violet-500">{tpl.items?.length || 0} itens · {tpl.recurrence === "daily" ? "Diário" : "Semanal"}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => applyTemplate(tpl.id)} className="h-7 bg-violet-600 hover:bg-violet-700 text-white text-[10px] px-2" data-testid={`use-template-${tpl.id}`}>
                        <Copy size={12} className="mr-1" /> Usar Hoje
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteChecklist(tpl.id)} className="h-7 w-7 p-0 text-zinc-400 hover:text-red-500">
                        <Trash size={12} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Active checklists */}
      {filteredActive.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Em andamento ({filteredActive.length})</p>
          {filteredActive.map((cl) => {
            const config = TYPE_CONFIG[cl.checklist_type] || TYPE_CONFIG.custom;
            const Icon = config.icon;
            const checkedCount = cl.items.filter(i => i.checked).length;
            const progress = cl.items.length > 0 ? (checkedCount / cl.items.length) * 100 : 0;
            const isExpanded = expandedId === cl.id;
            
            return (
              <Card key={cl.id} className={`${config.border} shadow-sm`} data-testid={`checklist-${cl.id}`}>
                <CardContent className="p-0">
                  <button onClick={() => setExpandedId(isExpanded ? null : cl.id)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-zinc-50/50 transition-colors">
                    <div className={`w-9 h-9 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={18} className={config.color} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-zinc-900 truncate">{cl.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-semibold uppercase tracking-wider ${config.color}`}>{config.label}</span>
                        {cl.praca_name && <span className="text-[10px] font-bold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">{cl.praca_name}</span>}
                        <span className="text-[10px] text-zinc-400">{checkedCount}/{cl.items.length}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-zinc-900 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                      </div>
                      {isExpanded ? <CaretUp size={16} className="text-zinc-400" /> : <CaretDown size={16} className="text-zinc-400" />}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-zinc-100">
                      <div className="space-y-1 mt-3">
                        {cl.items.map((item, idx) => (
                          <div key={item.id} onClick={() => toggleItem(cl, idx)}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors cursor-pointer ${item.checked ? "bg-zinc-50" : "hover:bg-zinc-50"}`}
                            data-testid={`checklist-item-${cl.id}-${idx}`}>
                            <Checkbox checked={item.checked} className="pointer-events-none" />
                            <span className={`text-sm flex-1 ${item.checked ? "line-through text-zinc-400" : "text-zinc-800"}`}>{item.text}</span>
                            {item.category && <span className="text-[10px] text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">{item.category}</span>}
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between mt-3 pt-2 border-t border-zinc-100">
                        <div className="flex gap-1 items-center flex-wrap">
                          {pracas.length > 0 && (
                            <select value={cl.praca_id || "none"} onChange={(e) => assignToPraca(cl.id, e.target.value)}
                              className="h-7 text-[10px] border border-zinc-200 rounded px-1.5 bg-white" data-testid={`assign-praca-cl-${cl.id}`}>
                              <option value="none">Sem Praça</option>
                              {pracas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => saveAsTemplate(cl.id)} className="text-violet-600 hover:bg-violet-50 text-xs" data-testid={`save-template-${cl.id}`}>
                            <FloppyDisk size={14} className="mr-1" /> Template
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => shareChecklist(cl.id)} className="text-blue-600 hover:bg-blue-50 text-xs" data-testid={`share-checklist-${cl.id}`}>
                            <ShareNetwork size={14} className="mr-1" /> Compartilhar
                          </Button>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => deleteChecklist(cl.id)} className="text-red-500 hover:bg-red-50 text-xs" data-testid={`delete-checklist-${cl.id}`}>
                          <Trash size={14} className="mr-1" /> Remover
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Completed checklists */}
      {filteredCompleted.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Concluídas ({filteredCompleted.length})</p>
          {filteredCompleted.map((cl) => {
            const config = TYPE_CONFIG[cl.checklist_type] || TYPE_CONFIG.custom;
            return (
              <Card key={cl.id} className="border-zinc-100 opacity-60">
                <CardContent className="p-4 flex items-center gap-3">
                  <Check size={18} className="text-emerald-500" weight="bold" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-600 truncate">{cl.name}</p>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] ${config.color}`}>{config.label} · {cl.items.length} itens</span>
                      {cl.praca_name && <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-bold">{cl.praca_name}</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteChecklist(cl.id)} className="text-zinc-400 hover:text-red-500 h-7 w-7 p-0">
                    <Trash size={14} />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus size={20} /> Nova Checklist</DialogTitle>
            <DialogDescription className="sr-only">Criar nova checklist personalizada</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nome</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ex: Preparação para evento" className="mt-1" data-testid="checklist-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Itens</label>
              <div className="space-y-2 mt-1">
                {newItems.map((item, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input value={item} onChange={(e) => { const n = [...newItems]; n[idx] = e.target.value; setNewItems(n); }}
                      placeholder={`Item ${idx + 1}`} className="flex-1" data-testid={`checklist-new-item-${idx}`} />
                    {newItems.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-9 w-9 text-red-400" onClick={() => setNewItems(newItems.filter((_, i) => i !== idx))}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setNewItems([...newItems, ""])} className="w-full text-xs">
                  <Plus size={14} className="mr-1" /> Adicionar item
                </Button>
              </div>
            </div>
            <Button onClick={createCustom} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="create-checklist-btn">
              Criar Checklist
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Checklist from Sheets/Events Dialog */}
      <Dialog open={genDialog} onOpenChange={setGenDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lightning size={20} /> Gerar Checklist com IA</DialogTitle>
            <DialogDescription>Selecione fichas técnicas ou um evento para gerar uma checklist de produção automaticamente</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2 max-h-[400px] overflow-y-auto">
            {events.length > 0 && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-violet-700">Eventos</label>
                <div className="space-y-1 mt-1">
                  {events.map(ev => (
                    <div key={ev.id} role="button" tabIndex={0} onClick={() => {
                      setGenSelectedEvent(ev.id === genSelectedEvent ? "" : ev.id);
                      if (ev.id !== genSelectedEvent && ev.sheets) {
                        setGenSelectedIds(ev.sheets.map(s => s.sheet_id).filter(Boolean));
                      }
                    }}
                      className={`p-2 rounded-lg border text-sm cursor-pointer transition-all ${genSelectedEvent === ev.id ? 'border-violet-500 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                      data-testid={`gen-event-${ev.id}`}>
                      <span className="font-semibold">{ev.name}</span>
                      <span className="text-xs text-zinc-500 ml-2">{ev.num_guests ? `${ev.num_guests} convidados` : ""} {ev.sheets ? `· ${ev.sheets.length} fichas` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Fichas Técnicas</label>
              <div className="space-y-1 mt-1">
                {sheets.map(s => {
                  const sel = genSelectedIds.includes(s.id);
                  return (
                    <div key={s.id} role="button" tabIndex={0} onClick={() => setGenSelectedIds(prev => sel ? prev.filter(x => x !== s.id) : [...prev, s.id])}
                      className={`p-2 rounded-lg border text-sm cursor-pointer transition-all flex items-center gap-2 ${sel ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                      data-testid={`gen-sheet-${s.id}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${sel ? 'bg-zinc-900 border-zinc-900' : 'border-zinc-300'}`}>
                        {sel && <Check size={10} className="text-white" weight="bold" />}
                      </div>
                      <span className="truncate">{s.name}</span>
                      <span className="text-[10px] text-zinc-400 ml-auto">{s.yield_quantity} {s.yield_unit}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <Button onClick={generateFromSheets} disabled={genLoading} className="w-full bg-emerald-700 text-white hover:bg-emerald-800 mt-2" data-testid="gen-checklist-submit">
            {genLoading ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Gerando com IA...</> : <><Lightning size={16} className="mr-2" /> Gerar Checklist</>}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
